import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import jQuery from 'jquery';
import moment from 'moment';
import logError from 'App/babel/helpers/logError';
import AppService from 'App/legacy/AppService';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import ActivityResource from 'Resources/Activity';
import CommentResource from 'Resources/Comment';
import FieldTranslation from 'App/babel/resources/FieldTranslations';
import openModal from 'App/services/Modal';
import { openDrawer } from 'Services/Drawer';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditContact } from 'App/components/EditContact/openEditContact';
import { activityOutcomeTracker } from 'App/babel/helpers/Tracker';
import { DISQUALIFIED, statusDisqualifiable } from 'Components/Helpers/journeyStep';
import { TYPES } from 'Components/Helpers/SourceHelper';
import { Feature, isAvailable, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import {
	ANSWER_ACTIONS,
	FOUND_LEAD_ACTIONS,
	OUTCOME_TYPES,
	ANSWER_ACTIONS_GREAT
} from 'App/babel/enum/activityOutcome';

/**
 * The activity behind an expanded timeline row, plus the extras the outcome UI
 * reads off it. Loosely typed on purpose: `App/babel/components/EventActivity`
 * is a PropTypes component and the Activity resource returns the raw entity
 * with contact, titleCategories and requiredFieldsContact grafted on — exactly
 * as the Angular directive assembled `rootData.eventDetails`.
 */
export type ActivityDetails = Record<string, any>;

type Options = {
	activityId: number;
	/** Collapses the row, the way `closeAndApply` did in the Angular directive. */
	onClose: () => void;
};

const outcomeContactActions = [ANSWER_ACTIONS.TALK_TO_SOMEONE, FOUND_LEAD_ACTIONS.ADDED_CONTACT];

/**
 * A React port of `upsales/common/directives/event/upEventActivity.js` — the
 * ~825 lines of controller that fetched an activity when a timeline row was
 * expanded and supplied every callback that
 * `App/babel/components/EventActivity` takes.
 *
 * `EditOrder/components/EventActivity.tsx` holds an equivalent port for the
 * order modal, but its copy is welded to that modal's draft-state store and to
 * its own row rendering, so it is not callable from here. The duplication is
 * deliberate and tracked — see the note on WEB-8777.
 *
 * Reads go through `activityRef` rather than the state variable: these
 * callbacks are handed to a class component and to Redux-connected children
 * that hold on to them, so a closure over `activity` would go stale after the
 * first change. Writes go through `patchActivity`, which replaces the object so
 * consumers see a new identity.
 */
const useTimelineActivityOutcome = ({ activityId, onClose }: Options) => {
	const [activity, setActivity] = useState<ActivityDetails | null>(null);
	const [isOutcomeEnabled, setIsOutcomeEnabled] = useState(false);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const activityRef = useRef<ActivityDetails | null>(null);
	activityRef.current = activity;

	const customerId = AppService.getCustomerId();
	const hasActivityOutcomeAccess = isAvailable(Feature.ACTIVITY_OUTCOMES);

	const patchActivity = useCallback((patch: ActivityDetails) => {
		const next = { ...(activityRef.current ?? {}), ...patch };
		activityRef.current = next;
		setActivity(next);
		return next;
	}, []);

	const tracker = useCallback((outcome?: ActivityDetails | null) => {
		if (!outcome) {
			return;
		}
		let datePreset = 'no_postpone';
		if (outcome.type === 'postpone' || outcome.outcome === 'postponed') {
			datePreset = outcome.date;
		}
		activityOutcomeTracker.track(activityOutcomeTracker.events.CREATE_EVENT, {
			location: 'timeline',
			type: outcome.type,
			outcome: outcome.outcome,
			date_preset: datePreset
		});
	}, []);

	const saveActivity = useCallback(
		(payload?: ActivityDetails) =>
			ActivityResource.save(payload ?? (activityRef.current as ActivityDetails)).catch(err =>
				logError(err, 'Failed to save activity from the timeline outcome expansion')
			),
		[]
	);

	const loadContact = useCallback(async (contactId: number) => {
		const Contact = getAngularModule('Contact');
		try {
			const { data } = await Contact.customer(AppService.getCustomerId()).get(contactId);
			const next = { ...(activityRef.current ?? {}), contact: data };
			activityRef.current = next;
			setActivity(next);
		} catch (err) {
			logError(err, 'Failed to load the contact behind a timeline activity');
		}
	}, []);

	const loadTitleCategories = useCallback(async () => {
		try {
			const { data } = await FieldTranslation.find({ type: 'titlecategory' });
			if (!data) {
				return;
			}
			const categories = data.map((entry: ActivityDetails, key: number) => ({
				id: key,
				name: entry.value,
				tagId: entry.tagId,
				type: entry.type,
				usedCount: entry.usedCount
			}));
			categories.push({
				id: categories.length + 1,
				name: Tools.$translate('default.removeTitle'),
				type: 'remove',
				usedCount: null
			});
			const next = { ...(activityRef.current ?? {}), titleCategories: categories };
			activityRef.current = next;
			setActivity(next);
		} catch (err) {
			logError(err, 'Failed to load title categories for the timeline outcome expansion');
		}
	}, []);

	// The row only mounts this once it is expanded, so the fetch is the lazy load
	// the Angular `expand()` did before flipping `$$expand`.
	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				const { data } = await ActivityResource.get(activityId);
				if (cancelled || !data) {
					setLoading(false);
					return;
				}

				const details: ActivityDetails = {
					...data,
					requiredFieldsContact: AppService.getMetadata().requiredFields.Contact
				};
				activityRef.current = details;
				setActivity(details);
				setLoading(false);

				const activityTypes = AppService.getActivityTypes('activity', true);
				const todoTypes = AppService.getTodoTypes();
				if (details.activityType) {
					setIsOutcomeEnabled(
						_.get(
							_.find({ ...activityTypes, ...todoTypes }, { id: details.activityType.id }),
							'hasOutcome',
							false
						)
					);
				}

				if (details.contacts?.length) {
					loadContact(details.contacts[0].id);
				}
				loadTitleCategories();
			} catch (err) {
				if (!cancelled) {
					setLoading(false);
					logError(err, 'Failed to load the activity behind a timeline row');
				}
			}
		};

		load();

		return () => {
			cancelled = true;
		};
	}, [activityId, loadContact, loadTitleCategories]);

	const createCommentIfExists = useCallback(async (commentDesc?: string) => {
		const current = activityRef.current;
		if (!commentDesc || !current) {
			return undefined;
		}
		const { data } = await CommentResource.save({
			description: commentDesc,
			client: current.contact?.client || current.client,
			activity: current,
			user: AppService.getSelf(),
			outcomeType: current.outcome?.type
		} as never);
		return data;
	}, []);

	const openActivity = useCallback(() => {
		const current = activityRef.current;
		const todoTypes = AppService.getTodoTypes();
		const activityTypeId = current?.activityType?.id;

		if (activityTypeId === todoTypes.TODO.id) {
			openDrawer('EditTodo', { todo: { id: activityId } });
			return;
		}
		if (activityTypeId === todoTypes.PHONE_CALL?.id) {
			openDrawer('EditPhonecall', {
				activity: { ...current, id: activityId }
			});
			return;
		}
		Tools.$upModal.open('editActivity', { id: activityId });
	}, [activityId]);

	const saveContact = useCallback(
		(contact: ActivityDetails | null) => {
			const patch: ActivityDetails = contact ? { contacts: [contact] } : { contacts: null, contact: null };

			if (hasActivityOutcomeAccess) {
				const outcome = activityRef.current?.outcome;
				if (outcome && !outcomeContactActions.includes(outcome.outcome)) {
					patch.outcome = null;
				}
			}

			const next = patchActivity(patch);
			if (contact) {
				loadContact(contact.id);
			}
			saveActivity(next);
		},
		[hasActivityOutcomeAccess, loadContact, patchActivity, saveActivity]
	);

	const createContact = useCallback(() => {
		openEditContact({ customerId, account: activityRef.current?.client })
			.then((contact: ActivityDetails) => {
				if (contact?.active) {
					saveContact(contact);
				}
			})
			.catch(err => logError(err, 'Failed to create a contact from the timeline outcome expansion'));
	}, [customerId, saveContact]);

	const editContact = useCallback(() => {
		openEditContact({ contact: _.cloneDeep(activityRef.current?.contact), customerId })
			.then((contact: ActivityDetails) => patchActivity({ contact }))
			.catch(err => logError(err, 'Failed to edit a contact from the timeline outcome expansion'));
	}, [customerId, patchActivity]);

	const setJourneyStep = useCallback(
		(journeyStep: string) => {
			const Contact = getAngularModule('Contact');
			return Contact.customer(customerId)
				.save({ id: activityRef.current?.contact?.id, journeyStep })
				.then((res: ActivityDetails) =>
					patchActivity({ contact: { ...activityRef.current?.contact, journeyStep: res.data.journeyStep } })
				)
				.catch((err: Error) => logError(err, 'Failed to set the contact journey step'));
		},
		[customerId, patchActivity]
	);

	const updateContact = useCallback(
		(value: string, type: string) => {
			const current = activityRef.current;
			if (!current?.contact) {
				return;
			}
			const contact = { ...current.contact };

			if (type === 'titleCategory') {
				contact.titleCategory = (current.titleCategories ?? []).filter(
					(category: ActivityDetails) => value === category.name
				);
			} else if (type === 'title') {
				contact.title = value;
			} else if (type === 'cellphone') {
				contact.cellPhone = value;
			} else if (type === 'phone') {
				contact.phone = value;
			} else if (type === 'email') {
				contact.email = value;
			}

			patchActivity({ contact });

			const Contact = getAngularModule('Contact');
			Tools.ScriptService.contact
				.save(contact)
				.then(() =>
					Contact.customer(customerId)
						.save(contact)
						.then((response: ActivityDetails) => patchActivity({ contact: response.data }))
				)
				.catch((err: Error) => logError(err, 'Failed to save the contact from the timeline outcome expansion'));
		},
		[customerId, patchActivity]
	);

	const updateNotes = useCallback(
		(notes: string) => {
			const current = activityRef.current;
			if (!current) {
				return;
			}
			patchActivity({ notes });
			// Only the notes, matching the Angular directive — a full save here would
			// write back a half-edited outcome.
			saveActivity({ id: current.id, notes });
		},
		[patchActivity, saveActivity]
	);

	const showMoreOptIns = useCallback(() => {
		openModal('ListModal', {
			title: 'default.optIns',
			columns: [
				{ title: 'default.name', value: 'title' },
				{ title: 'default.regDate', value: 'date' }
			],
			data: (activityRef.current?.contact?.optins ?? []).map((optin: ActivityDetails) => ({
				...optin,
				date: moment(optin.regDate).format('L LT')
			}))
		});
	}, []);

	const setOutcome = useCallback(
		(outcome: ActivityDetails, cb: () => void = () => {}) => {
			patchActivity({
				outcome: {
					...outcome,
					date: moment.utc().format(),
					clientJourney: activityRef.current?.client?.journeyStep
				}
			});
			cb();
		},
		[patchActivity]
	);

	const addDate = useCallback(
		(date: Date) => {
			const time = activityRef.current?.time;
			if (time && typeof time !== 'string') {
				date.setHours(time.getHours());
				date.setMinutes(time.getMinutes());
			}
			patchActivity({ date });
		},
		[patchActivity]
	);

	const onPresetDateChange = useCallback(
		(date: Date) => {
			patchActivity({ time: null });
			addDate(date);
		},
		[addDate, patchActivity]
	);

	const updateTime = useCallback(
		(time: Date | string | null) => {
			const current = activityRef.current;
			let parsed = typeof time === 'string' ? new Date(time) : time;
			const date = typeof current?.date === 'string' ? new Date(current.date) : current?.date;

			if (parsed && date) {
				date.setHours(parsed.getHours(), parsed.getMinutes(), 0, 0);
			}
			patchActivity({
				date,
				showTime: !!parsed,
				time: parsed ? moment(parsed).format('HH:mm:ss') : parsed
			});
		},
		[patchActivity]
	);

	const onSetJourneyStepClient = useCallback(
		(journeyStep: string) => {
			const account = activityRef.current?.client;
			if (!account) {
				return Promise.resolve();
			}
			const Account = getAngularModule('Account');
			return Account.customer(customerId)
				.save({ id: account.id, journeyStep })
				.then((res: ActivityDetails) =>
					patchActivity({ client: { ...account, journeyStep: res.data.journeyStep } })
				)
				.catch((err: Error) => logError(err, 'Failed to set the company journey step'));
		},
		[customerId, patchActivity]
	);

	const setDisqualifyToClient = useCallback(() => {
		const current = activityRef.current;
		const journeyStep = current?.client?.journeyStep;
		if (current?.outcome?.disqualify && journeyStep && statusDisqualifiable(journeyStep)) {
			onSetJourneyStepClient(DISQUALIFIED);
		}
	}, [onSetJourneyStepClient]);

	const confirm = useCallback(() => {
		setSaving(true);
		const current = activityRef.current;
		if (!current) {
			return;
		}
		let closeDate = current.closeDate;

		if (hasActivityOutcomeAccess && current.outcome) {
			setDisqualifyToClient();
			if (current.outcome.closeActivity ?? false) {
				closeDate = new Date();
			}
		}

		onClose();

		ActivityResource.save({
			id: current.id,
			closeDate,
			outcome: current.outcome,
			date: current.date,
			time: current.time
		} as never)
			.then(() => tracker(current.outcome))
			.catch(err => logError(err, 'Failed to save the activity outcome'))
			.finally(() => setSaving(false));
	}, [hasActivityOutcomeAccess, onClose, setDisqualifyToClient, tracker]);

	const createActivityFollowUp = useCallback(
		async (isAppointment: boolean) => {
			const current = activityRef.current;
			if (!current) {
				return;
			}
			// `.new()` still comes from the Angular resources: they seed the entity
			// with the org's default type and run it through ParseGeneric for custom
			// field defaults, neither of which the modern resources do.
			const Activity = getAngularModule('Activity');
			const Appointment = getAngularModule('Appointment');

			const item = isAppointment ? Appointment.new().data : Activity.new().data;
			item.activityType = isAppointment ? null : current.activityType;
			item.notes = current.notes;
			item.client = current.client;
			item.project = current.project;
			item.opportunity = current.opportunity;
			item.description = current.description;
			item.users = [_.pick(AppService.getSelf(), ['id', 'name', 'email'])];

			const closed = patchActivity({ closeDate: moment.utc().format() });

			if (isAppointment) {
				item.contacts = current.contact ? [current.contact] : null;
				await openEditAppointment({ appointment: item });
				onClose();
				await saveActivity(closed);
				tracker(closed.outcome);
				return;
			}

			item.contacts = current.contacts;
			if (hasSoftDeployAccess('TODO_LIST') && item.activityType?.name === 'Phonecall') {
				try {
					const res = await ActivityResource.save(item);
					onClose();
					if (res) {
						openDrawer('EditPhonecall', { activity: res.data });
						saveActivity(closed);
					}
				} catch (err) {
					logError(err, 'Failed to save activity');
				}
				return;
			}

			onClose();
			await Tools.$upModal.open('editActivity', { activity: item });
			saveActivity(closed);
		},
		[onClose, patchActivity, saveActivity, tracker]
	);

	const createCallFollowUp = useCallback(() => {
		const closed = patchActivity({ closeDate: moment.utc().format() });
		onClose();
		openDrawer('CreateCall', {
			initialDescription: closed.description,
			client: closed.client,
			contact: closed.contact,
			notes: closed.notes,
			opportunity: closed.opportunity,
			// The drawers type this as a full Activity; what the expansion holds is
			// the raw entity the resource returned, same as the directive passed.
			activity: closed as never,
			// TYPES.ACTIVITY, not the directive's `TYPES.activity` — SourceHelper
			// exports upper-case keys, so the Angular version passed `undefined`
			// here. EditOrder's port already uses the working one.
			source: { id: closed.id, type: TYPES.ACTIVITY },
			onSave: () => saveActivity(closed)
		});
	}, [onClose, patchActivity, saveActivity]);

	const createTodoFollowUp = useCallback(() => {
		const closed = patchActivity({ closeDate: moment.utc().format() });
		onClose();
		openDrawer('CreateTodo', {
			description: closed.description,
			client: closed.client,
			contact: closed.contact,
			notes: closed.notes,
			opportunity: closed.opportunity,
			// The drawers type this as a full Activity; what the expansion holds is
			// the raw entity the resource returned, same as the directive passed.
			activity: closed as never,
			// TYPES.ACTIVITY, not the directive's `TYPES.activity` — SourceHelper
			// exports upper-case keys, so the Angular version passed `undefined`
			// here. EditOrder's port already uses the working one.
			source: { id: closed.id, type: TYPES.ACTIVITY },
			onSave: () => saveActivity(closed)
		});
	}, [onClose, patchActivity, saveActivity]);

	const bookAppointment = useCallback(
		async (commentDesc?: string) => {
			const current = activityRef.current;
			if (!current) {
				return;
			}
			const Appointment = getAngularModule('Appointment');
			const appointment = Appointment.new().data;

			appointment.notes = current.notes;
			appointment.client = current.client;
			appointment.project = current.project;
			appointment.opportunity = current.opportunity;
			appointment.users = [_.pick(AppService.getSelf(), ['id', 'name', 'email'])];
			appointment.activityType = null;
			appointment.contacts = current.contact ? [current.contact] : null;

			patchActivity({ closeDate: moment.utc().format() });

			const saved = (await openEditAppointment({ appointment })) ?? {};
			const comment = await createCommentIfExists(commentDesc);

			setOutcome({
				type: OUTCOME_TYPES.ANSWER,
				outcome: ANSWER_ACTIONS_GREAT.BOOK_APPOINTMENT,
				appointmentId: _.get(saved, 'id'),
				...(comment ? { commentId: comment.id } : {})
			});
			onClose();
			saveActivity();
		},
		[createCommentIfExists, onClose, patchActivity, saveActivity, setOutcome]
	);

	const setAppointment = useCallback(
		(id: number, notes: string) => {
			if (!id) {
				return;
			}
			const closed = patchActivity({ closeDate: moment.utc().format() });
			onClose();
			saveActivity(closed)
				.then(() => openEditAppointment({ id, appointment: { notes: `${notes}\n${closed.notes}` } }))
				.then(() => tracker(closed.outcome))
				.catch(err => logError(err, 'Failed to save the activity before opening the appointment'));
		},
		[onClose, patchActivity, saveActivity, tracker]
	);

	const planPhonecall = useCallback(
		(commentDesc?: string) => {
			const closed = patchActivity({
				closeDate: moment.utc().format(),
				outcome: { type: OUTCOME_TYPES.ANSWER, outcome: ANSWER_ACTIONS_GREAT.PLAN_PHONE_CALL }
			});

			openDrawer('CreateCall', {
				contact: closed.contact,
				client: closed.client,
				// TYPES.ACTIVITY, not the directive's `TYPES.activity` — SourceHelper
				// exports upper-case keys, so the Angular version passed `undefined`
				// here. EditOrder's port already uses the working one.
				source: { id: closed.id, type: TYPES.ACTIVITY },
				onSave: async (created: ActivityDetails) => {
					const comment = await createCommentIfExists(commentDesc);
					const current = activityRef.current as ActivityDetails;
					onClose();
					saveActivity({
						...current,
						outcome: {
							...current.outcome,
							activityId: created.id,
							...(comment ? { commentId: comment.id } : {})
						}
					});
				}
			});
		},
		[createCommentIfExists, onClose, patchActivity, saveActivity]
	);

	const createOrderAndSave = useCallback(() => {
		const current = activityRef.current;
		if (!current) {
			return Promise.resolve();
		}
		const closed = patchActivity({ closeDate: moment.utc().format() });
		onClose();

		return openEditOrder({
			customerId,
			clientId: current.client?.id ?? null,
			contactId: current.contact?.id ?? null,
			campaign: current.project ?? null,
			notes: current.notes,
			type: 'order',
			resolveOnSave: true
		})
			.then((order: ActivityDetails) => {
				const next = patchActivity({
					opportunity: order,
					outcome: { type: OUTCOME_TYPES.ANSWER, outcome: ANSWER_ACTIONS_GREAT.CREATE_ORDER }
				});
				return saveActivity(next);
			})
			.finally(() => tracker(activityRef.current?.outcome ?? closed.outcome));
	}, [customerId, onClose, patchActivity, saveActivity, tracker]);

	const createOpportunityAndSave = useCallback(() => {
		const current = activityRef.current;
		if (!current) {
			return Promise.resolve();
		}
		return openEditOrder({
			customerId,
			clientId: current.client?.id ?? null,
			contactId: current.contact?.id ?? null,
			campaign: current.project ?? null,
			notes: current.notes,
			type: 'opportunity',
			resolveOnSave: true,
			activityId: null
		})
			.then((opportunity: ActivityDetails) => {
				const next = patchActivity({
					opportunity,
					closeDate: moment.utc().format(),
					outcome: { type: OUTCOME_TYPES.ANSWER, outcome: ANSWER_ACTIONS_GREAT.CREATE_OPPORTUNITY }
				});
				return saveActivity(next).then(onClose);
			})
			.catch(err => logError(err, 'Error saving opportunity'))
			.finally(() => tracker(activityRef.current?.outcome));
	}, [customerId, onClose, patchActivity, saveActivity, tracker]);

	// select2 lives outside React, so opening its dropdown and grafting a
	// "create a contact" button into it is still DOM work, exactly as it was in
	// the directive.
	const addNewContactButton = useCallback(() => {
		if (!hasActivityOutcomeAccess) {
			return;
		}
		setTimeout(() => {
			const select2Drop = jQuery('.select2-with-searchbox');
			const existing = jQuery('.add-new-contact-activity');
			const button = jQuery('<div class="add-new-contact-activity Button up-btn btn-link btn-block"/>').text(
				Tools.$translate('default.createAContact')
			);
			if (existing.length) {
				existing.remove();
			}
			select2Drop.append(button);
			select2Drop.on('click', '.add-new-contact-activity', () => {
				// @ts-expect-error select2's typings only declare 'open'
				jQuery('#contact-select').select2('close');
				createContact();
			});
		}, 100);
	}, [createContact, hasActivityOutcomeAccess]);

	const openContactDropdown = useCallback(() => {
		addNewContactButton();
		jQuery('#contact-select').select2('open');
	}, [addNewContactButton]);

	return {
		eventDetails: activity,
		loading,
		saving,
		isOutcomeEnabled,
		hasActivityOutcomeAccess,
		callbacks: {
			openActivity,
			showMoreOptIns,
			saveContact,
			createContact,
			editContact,
			setJourneyStep,
			updateContact,
			updateNotes,
			confirm,
			setOutcome,
			onPresetDateChange,
			addDate,
			updateTime,
			bookAppointment,
			openContactDropdown,
			addNewContactButton,
			createOrderAndSave,
			createOpportunityAndSave,
			setAppointment,
			planPhonecall,
			createActivityFollowUp,
			createTodoFollowUp,
			createCallFollowUp
		}
	};
};

export default useTimelineActivityOutcome;
